import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {    
    return (
        <div className="container mx-auto px-4 md:px-6 pt-24 pb-16">
            {/* SEO Settings */}
            <title>{`Privacy Policy – How ${process.env.REACT_APP_NAME} Protects Your Data`}</title>
            <meta
                name="description"
                content="Read Hunar Pool's privacy policy to understand how we collect, use, and protect your personal information while using our platform."
            />

            {/* Page Header */}
            <div className="text-center mb-12">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Privacy <span className="gradient-text">Policy</span>
                </h1>
                <p className="text-gray-300 text-lg">Last Updated: January 15, 2026</p>
            </div>

            {/* Quick Navigation */}
            <div className="section-bg rounded-2xl p-6 mb-8 border border-gray-800">
                <h2 className="text-xl font-bold mb-4">Quick Navigation</h2>
                <div className="flex flex-wrap gap-3">
                <a
                    href="#introduction"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Introduction
                </a>
                <a
                    href="#data-collection"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Data Collection
                </a>
                <a
                    href="#data-use"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Data Use
                </a>
                <a
                    href="#data-sharing"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Data Sharing
                </a>
                <a
                    href="#your-rights"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Your Rights
                </a>
                <a
                    href="#security"
                    className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                    Security
                </a>
                </div>
            </div>

            {/* Policy Content */}
            <div className="section-bg rounded-2xl p-8 border border-gray-800 content-section">
                <div id="introduction" className="mb-12">
                <h2>1. Introduction</h2>
                <p>
                    Welcome to Hunar Pool. We are committed to protecting your personal
                    information and your right to privacy. This Privacy Policy explains how
                    we collect, use, disclose, and safeguard your information when you use
                    our website and services.
                </p>
                <p>
                    By accessing or using Hunar Pool, you agree to the collection and use of
                    information in accordance with this Privacy Policy. If you disagree with
                    any part of this policy, please do not use our services.
                </p>
                </div>
                <div id="data-collection" className="mb-12">
                <h2>2. Information We Collect</h2>
                <h3>2.1 Personal Information</h3>
                <p>When you create an account or use our services, we may collect:</p>
                <ul>
                    <li>Name, email address, and contact information</li>
                    <li>Professional information (resume, work experience, education)</li>
                    <li>Skills, certifications, and professional preferences</li>
                    <li>Job search preferences and criteria</li>
                    <li>Location data (for job matching)</li>
                </ul>
                <h3>2.2 Automated Information</h3>
                <p>
                    We automatically collect certain information when you visit our website:
                </p>
                <ul>
                    <li>IP address and device information</li>
                    <li>Browser type and version</li>
                    <li>Pages visited and time spent on pages</li>
                    <li>Search queries and job applications</li>
                    <li>Cookies and similar tracking technologies</li>
                </ul>
                <h3>2.3 Information from Third Parties</h3>
                <p>We may receive information about you from:</p>
                <ul>
                    <li>LinkedIn or other professional networks (with your permission)</li>
                    <li>Employers and recruitment agencies</li>
                    <li>Analytics providers and advertising partners</li>
                </ul>
                </div>
                <div id="data-use" className="mb-12">
                <h2>3. How We Use Your Information</h2>
                <p>We use the information we collect for various purposes:</p>
                <ul>
                    <li>
                    <strong>Job Matching:</strong> To provide personalized job
                    recommendations using AI algorithms
                    </li>
                    <li>
                    <strong>Account Management:</strong> To create and maintain your user
                    account
                    </li>
                    <li>
                    <strong>Service Improvement:</strong> To develop and improve our AI
                    matching technology
                    </li>
                    <li>
                    <strong>Communication:</strong> To send job alerts, updates, and
                    important notifications
                    </li>
                    <li>
                    <strong>Analytics:</strong> To analyze usage patterns and optimize
                    user experience
                    </li>
                    <li>
                    <strong>Security:</strong> To protect against fraudulent or
                    unauthorized activity
                    </li>
                    <li>
                    <strong>Legal Compliance:</strong> To comply with applicable laws and
                    regulations
                    </li>
                </ul>
                </div>
                <div id="data-sharing" className="mb-12">
                <h2>4. Information Sharing and Disclosure</h2>
                <p>We may share your information in the following situations:</p>
                <h3>4.1 With Your Consent</h3>
                <p>
                    We share your profile information with employers when you apply for jobs
                    or express interest in opportunities.
                </p>
                <h3>4.2 Service Providers</h3>
                <p>
                    We may share information with third-party vendors who perform services
                    on our behalf (hosting, analytics, email delivery).
                </p>
                <h3>4.3 Legal Requirements</h3>
                <p>
                    We may disclose information if required by law, court order, or
                    government request.
                </p>
                <h3>4.4 Business Transfers</h3>
                <p>
                    In connection with any merger, sale of company assets, or acquisition,
                    your information may be transferred.
                </p>
                <h3>4.5 Aggregated Data</h3>
                <p>
                    We may share aggregated, anonymized data that cannot be used to identify
                    you individually.
                </p>
                </div>
                <div id="your-rights" className="mb-12">
                <h2>5. Your Data Protection Rights</h2>
                <p>Depending on your location, you may have the following rights:</p>
                <ul>
                    <li>
                    <strong>Access:</strong> Request copies of your personal data
                    </li>
                    <li>
                    <strong>Rectification:</strong> Request correction of inaccurate
                    information
                    </li>
                    <li>
                    <strong>Erasure:</strong> Request deletion of your personal data
                    </li>
                    <li>
                    <strong>Restriction:</strong> Request restriction of processing your
                    data
                    </li>
                    <li>
                    <strong>Objection:</strong> Object to our processing of your data
                    </li>
                    <li>
                    <strong>Portability:</strong> Request transfer of your data to another
                    organization
                    </li>
                    <li>
                    <strong>Withdraw Consent:</strong> Withdraw your consent at any time
                    </li>
                </ul>
                <p>
                    To exercise these rights, please contact us at privacy@hunarpool.com.
                </p>
                </div>
                <div id="security" className="mb-12">
                <h2>6. Data Security</h2>
                <p>
                    We implement appropriate technical and organizational security measures
                    to protect your personal information against unauthorized access,
                    alteration, disclosure, or destruction. However, no internet
                    transmission is completely secure, and we cannot guarantee absolute
                    security.
                </p>
                <h3>6.1 Security Measures Include:</h3>
                <ul>
                    <li>Encryption of sensitive data in transit and at rest</li>
                    <li>Regular security assessments and penetration testing</li>
                    <li>Access controls and authentication mechanisms</li>
                    <li>Employee training on data protection</li>
                    <li>Regular security updates and patches</li>
                </ul>
                </div>
                <div className="mb-12">
                <h2>7. Data Retention</h2>
                <p>
                    We retain your personal information only for as long as necessary to
                    fulfill the purposes outlined in this Privacy Policy, unless a longer
                    retention period is required or permitted by law.
                </p>
                <p>
                    Account data is typically retained for 3 years after account inactivity.
                    You may request account deletion at any time.
                </p>
                </div>
                <div className="mb-12">
                <h2>8. Cookies and Tracking Technologies</h2>
                <p>
                    We use cookies and similar tracking technologies to track activity on
                    our website and store certain information. You can instruct your browser
                    to refuse all cookies or to indicate when a cookie is being sent.
                </p>
                <p>
                    Essential cookies are necessary for the website to function and cannot
                    be switched off. You can manage your cookie preferences through your
                    browser settings.
                </p>
                </div>
                <div className="mb-12">
                <h2>9. International Data Transfers</h2>
                <p>
                    Your information may be transferred to and maintained on computers
                    located outside of your state, province, country, or other governmental
                    jurisdiction where data protection laws may differ.
                </p>
                <p>
                    We ensure appropriate safeguards are in place for international data
                    transfers, including Standard Contractual Clauses where applicable.
                </p>
                </div>
                <div className="mb-12">
                <h2>10. Children's Privacy</h2>
                <p>
                    Our services are not intended for individuals under the age of 16. We do
                    not knowingly collect personal information from children under 16. If
                    you become aware that a child has provided us with personal information,
                    please contact us.
                </p>
                </div>
                <div className="mb-12">
                <h2>11. Changes to This Privacy Policy</h2>
                <p>
                    We may update our Privacy Policy from time to time. We will notify you
                    of any changes by posting the new Privacy Policy on this page and
                    updating the "Last Updated" date.
                </p>
                <p>
                    You are advised to review this Privacy Policy periodically for any
                    changes. Changes become effective when they are posted on this page.
                </p>
                </div>
                <div>
                <h2>12. Contact Us</h2>
                <p>
                    If you have any questions about this Privacy Policy, please contact us:
                </p>
                <ul>
                    <li>
                    <strong>Email:</strong> privacy@hunarpool.com
                    </li>
                    <li>
                    <strong>Address:</strong> Hunar Pool, 123 Tech Street, San Francisco,
                    CA 94107
                    </li>
                    <li>
                    <strong>Phone:</strong> +1 (555) 123-4567
                    </li>
                </ul>
                <p>
                    We typically respond to privacy-related inquiries within 7 business
                    days.
                </p>
                </div>
            </div>
        </div>        
    );
};



export default PrivacyPolicy;